import { memo, useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList, ReferenceLine,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';
import { parseInnoUseActors, parseActorsDevUse, parseIPSRActors } from '../../../utils/dataProcessor/innovationParsers';
import { Eye, Search, X, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const truncate = (str, len = 30) => str.length > len ? str.substring(0, len) + '...' : str;
const niceStep = (max) => {
  const rough = max / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const residual = rough / mag;
  const nice = residual <= 1.5 ? 1 : residual <= 3.5 ? 2 : residual <= 7.5 ? 5 : 10;
  return Math.max(1, nice * mag);
};
const compact = (v) => {
  if (v >= 1e6) return (v / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (v >= 1e3) return (v / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(v);
};

function buildPreviewRows(results) {
  const rows = [];
  for (const r of results) {
    let actors = parseInnoUseActors(r.innoUseActors);
    const innoSource = actors.length > 0 ? 'Inno Use Actors' : null;
    if (actors.length === 0) {
      actors = parseActorsDevUse(r.actorsDevUse);
    }
    const devSource = !innoSource && actors.length > 0 ? 'Actors Dev/Use' : null;
    const ipsrActors = parseIPSRActors(r.ipsrActors);
    if (actors.length === 0 && ipsrActors.length === 0) continue;

    // Merge per-row (same logic as useInnovationData)
    const rowMap = {};
    const sourceMap = {};
    for (const a of actors) {
      rowMap[a.actorType] = { ...a };
      sourceMap[a.actorType] = innoSource || devSource;
    }
    for (const a of ipsrActors) {
      const existing = rowMap[a.actorType];
      if (existing) {
        existing.total = Math.max(existing.total, a.total);
        if (a.women > 0 || a.men > 0) {
          existing.women = a.women;
          existing.men = a.men;
          existing.womenYouth = a.womenYouth;
          existing.menYouth = a.menYouth;
        }
        sourceMap[a.actorType] = sourceMap[a.actorType] + ' + IPSR';
      } else {
        rowMap[a.actorType] = { ...a };
        sourceMap[a.actorType] = 'IPSR Actors';
      }
    }

    for (const [type, a] of Object.entries(rowMap)) {
      rows.push({
        resultCode: r.resultCode || '',
        title: r.title || '',
        actorType: type,
        total: a.total,
        women: a.women || 0,
        men: a.men || 0,
        youth: (a.womenYouth || 0) + (a.menYouth || 0),
        source: sourceMap[type],
      });
    }
  }
  return rows;
}

const SORT_COLS = {
  resultCode: 'string', title: 'string', actorType: 'string',
  total: 'number', women: 'number', men: 'number', youth: 'number',
  source: 'string',
};

function ActorDataModal({ rows, onClose }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(SORT_COLS[key] === 'number' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const handleKeyDown = useCallback((e) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [handleKeyDown]);

  const filtered = useMemo(() => {
    let out = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter(r =>
        r.resultCode.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.actorType.toLowerCase().includes(q) ||
        r.source.toLowerCase().includes(q)
      );
    }
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      out = [...out].sort((a, b) => {
        if (SORT_COLS[sortKey] === 'number') return (a[sortKey] - b[sortKey]) * dir;
        return String(a[sortKey]).localeCompare(String(b[sortKey])) * dir;
      });
    }
    return out;
  }, [rows, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageRows = filtered.slice((page - 1) * perPage, page * perPage);
  useEffect(() => { setPage(1); }, [search, perPage]);

  const SortTh = ({ col, align, children }) => {
    const active = sortKey === col;
    const Icon = active ? (sortDir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th
        className={`px-3 py-2 font-medium cursor-pointer select-none ${align === 'right' ? 'text-right' : ''} ${active ? 'text-indigo-700' : 'text-[var(--text-secondary)] hover:text-indigo-600'}`}
        onClick={() => toggleSort(col)}
      >
        <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
          {children}
          <Icon className={`w-3.5 h-3.5 ${active ? 'text-indigo-600' : 'text-gray-400'}`} />
        </span>
      </th>
    );
  };

  const countLabel = search.trim() ? `${filtered.length} of ${rows.length}` : `${rows.length}`;

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex flex-col w-full max-w-6xl max-h-[80vh] bg-[var(--surface-primary)] rounded-xl shadow-2xl overflow-hidden mx-4">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border-primary)]">
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Actor Data Preview ({countLabel} entries)</h3>
            <p className="text-xs text-[var(--text-tertiary)]">Per-result actor rows after deduplication across source columns</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-tertiary)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-[var(--border-primary)]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-tertiary)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by result code, title, actor type, or source..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[var(--border-primary)] bg-[var(--surface-primary)] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col className="w-[9%]" />
              <col className="w-[22%]" />
              <col className="w-[20%]" />
              <col className="w-[10%]" />
              <col className="w-[9%]" />
              <col className="w-[9%]" />
              <col className="w-[7%]" />
              <col className="w-[14%]" />
            </colgroup>
            <thead className="sticky top-0 bg-[var(--surface-secondary)] text-left">
              <tr>
                <SortTh col="resultCode">Code</SortTh>
                <SortTh col="title">Title</SortTh>
                <SortTh col="actorType">Actor Type</SortTh>
                <SortTh col="total" align="right">How Many</SortTh>
                <SortTh col="women" align="right">Women</SortTh>
                <SortTh col="men" align="right">Men</SortTh>
                <SortTh col="youth" align="right">Youth</SortTh>
                <SortTh col="source">Source</SortTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-primary)]">
              {pageRows.map((r, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 text-[var(--text-primary)] font-mono text-xs">{r.resultCode || '—'}</td>
                  <td className="px-3 py-2 text-[var(--text-primary)] text-xs break-words">{r.title || '—'}</td>
                  <td className="px-3 py-2 text-[var(--text-primary)] text-xs break-words">{r.actorType}</td>
                  <td className="px-3 py-2 text-[var(--text-primary)] text-xs text-right tabular-nums">{r.total.toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs text-right tabular-nums" style={{ color: r.women > 0 ? '#be185d' : 'var(--text-tertiary)' }}>{r.women.toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs text-right tabular-nums" style={{ color: r.men > 0 ? '#1d4ed8' : 'var(--text-tertiary)' }}>{r.men.toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs text-right tabular-nums" style={{ color: r.youth > 0 ? '#047857' : 'var(--text-tertiary)' }}>{r.youth.toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs text-[var(--text-tertiary)]">{r.source}</td>
                </tr>
              ))}
              {pageRows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-[var(--text-tertiary)]">No results match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border-primary)] text-xs text-[var(--text-secondary)]">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} className="px-2 py-1 rounded border border-[var(--border-primary)] bg-[var(--surface-primary)] text-[var(--text-primary)] text-xs focus:outline-none">
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="p-1 rounded hover:bg-[var(--surface-hover)] disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} className="p-1 rounded hover:bg-[var(--surface-hover)] disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export const InnovationUsersChart = memo(function InnovationUsersChart() {
  const { userBreakdownData, innovationResults } = useInnovationData();
  const [showModal, setShowModal] = useState(false);
  const yAxisWidth = useResponsiveYAxisWidth(140);
  const previewRows = useMemo(() => buildPreviewRows(innovationResults), [innovationResults]);
  if (!userBreakdownData || userBreakdownData.grandTotal === 0) return (
    <ChartWrapper title="Innovation Users & Reach">
      <NoChartData message="No actor or reach data reported" detail='This chart aggregates reach figures from the actor data fields, showing totals by actor type with gender breakdowns. None of the innovation results in the current selection contain actor/reach information.' />
    </ChartWrapper>
  );

  const { byActorType, grandTotal, grandWomen, grandMen, grandYouth, resultsWithActorData, resultsWithGenderData } = userBreakdownData;

  const barData = byActorType.slice(0, 12).map(d => ({
    name: truncate(d.actorType),
    fullName: d.actorType,
    total: d.total,
    women: d.women,
    men: d.men,
    youth: d.youth,
  }));

  // Butterfly chart data: women as negative, men as positive
  const butterflySlice = byActorType.slice(0, 12);
  const butterflyData = butterflySlice.map(d => ({
    name: truncate(d.actorType),
    fullName: d.actorType,
    women: -(d.women || 0),
    men: d.men || 0,
    womenAbs: d.women || 0,
    menAbs: d.men || 0,
  }));
  const butterflyRawMax = Math.max(...butterflySlice.map(d => Math.max(d.women || 0, d.men || 0)), 1);
  const step = niceStep(butterflyRawMax);
  const butterflyMax = Math.ceil(butterflyRawMax * 1.15 / step) * step;
  const butterflyTicks = [];
  for (let t = -butterflyMax; t <= butterflyMax; t += step) butterflyTicks.push(t);

  const exportData = byActorType.map(d => ({
    'Actor Type': d.actorType,
    Total: d.total,
    Women: d.women,
    Men: d.men,
    Youth: d.youth,
  }));

  return (
    <ChartWrapper title="Innovation Users & Reach" info="Reported reach of innovation results: total people, women, men, and youth reached, broken down by actor type. Data sourced from actor information fields across all innovation types." chartData={exportData} exportFilename="innovation-users-reach" sourceResults={innovationResults}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-2">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{grandTotal.toLocaleString()}</p>
          <p className="text-sm text-blue-600">Total people reached</p>
        </div>
        <div className="bg-pink-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-pink-700">{grandWomen.toLocaleString()}</p>
          <p className="text-sm text-pink-600">Women</p>
        </div>
        <div className="bg-sky-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-sky-700">{grandMen.toLocaleString()}</p>
          <p className="text-sm text-sky-600">Men</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-emerald-700">{grandYouth.toLocaleString()}</p>
          <p className="text-sm text-emerald-600">Youth</p>
        </div>
      </div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs text-gray-400">
          Based on {resultsWithActorData.toLocaleString()} of {innovationResults.length.toLocaleString()} innovation results that report actor data
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
        >
          <Eye className="w-3.5 h-3.5" />
          Preview data
        </button>
      </div>

      <div className="space-y-6">
        {barData.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Reach by Actor Type</h5>
            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
              Total reported reach across {resultsWithActorData.toLocaleString()} innovation results with actor data.
            </div>
            <ResponsiveContainer width="100%" height={Math.max(250, barData.length * 32)}>
              <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => v.toLocaleString()} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: yAxisWidth < 140 ? 9 : 11 }} width={yAxisWidth} />
                <Tooltip
                  formatter={(value) => value.toLocaleString()}
                  labelFormatter={(label) => {
                    const item = barData.find(d => d.name === label);
                    return item ? item.fullName : label;
                  }}
                  contentStyle={getTooltipStyle()}
                />
                <Bar dataKey="total" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="total" position="right" style={LABEL_STYLE} formatter={(v) => compact(v)} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {butterflyData.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Gender Breakdown by Actor Type</h5>
            <div className="mb-3 px-3 py-2 bg-pink-50 border border-pink-200 rounded-lg text-xs text-pink-700">
              Gender split where reported — {resultsWithGenderData.toLocaleString()} of {resultsWithActorData.toLocaleString()} results include gender figures ({resultsWithActorData > 0 ? Math.round(resultsWithGenderData / resultsWithActorData * 100) : 0}% coverage).
            </div>
            <div className="flex justify-between text-xs font-medium text-gray-500 mb-1 px-2">
              <span className="text-pink-600">← Women</span>
              <span className="text-blue-600">Men →</span>
            </div>
            <ResponsiveContainer width="100%" height={Math.max(250, butterflyData.length * 32)}>
              <BarChart data={butterflyData} layout="vertical" margin={{ left: 40, right: 40 }} stackOffset="sign">
                <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => Math.abs(v).toLocaleString()}
                  domain={[-butterflyMax, butterflyMax]}
                  allowDataOverflow
                  ticks={butterflyTicks}
                />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={100} />
                <ReferenceLine x={0} stroke="#374151" strokeWidth={2} />
                <Tooltip
                  contentStyle={getTooltipStyle()}
                  labelFormatter={(label) => {
                    const item = butterflyData.find(d => d.name === label);
                    return item ? item.fullName : label;
                  }}
                  formatter={(value, name) => [Math.abs(value).toLocaleString(), name]}
                />
                <Bar dataKey="women" fill="#ec4899" name="Women" radius={[4, 0, 0, 4]} stackId="gender">
                  <LabelList
                    content={(props) => {
                      const { x, y, width, height, index } = props;
                      const entry = butterflyData[index];
                      if (!entry || !entry.womenAbs) return null;
                      return (
                        <text x={x + width - 4} y={y + height / 2} textAnchor="end" dominantBaseline="central" fontSize={10} fill="#be185d">
                          {compact(entry.womenAbs)}
                        </text>
                      );
                    }}
                  />
                </Bar>
                <Bar dataKey="men" fill="#3b82f6" name="Men" radius={[0, 4, 4, 0]} stackId="gender">
                  <LabelList
                    content={(props) => {
                      const { x, y, width, height, index } = props;
                      const entry = butterflyData[index];
                      if (!entry || !entry.menAbs) return null;
                      return (
                        <text x={x + width + 4} y={y + height / 2} textAnchor="start" dominantBaseline="central" fontSize={10} fill="#1d4ed8">
                          {compact(entry.menAbs)}
                        </text>
                      );
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {showModal && <ActorDataModal rows={previewRows} onClose={() => setShowModal(false)} />}
    </ChartWrapper>
  );
});
